// Pezzi comuni degli appuntamenti ISEE / 730 (calendario CAF e pagine ISEE/730):
// scelta dell'orario libero, nuovo appuntamento, dettaglio (sposta / annulla / fatto).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, CalendarClock, Phone, Smartphone } from 'lucide-react';
import ClienteConDocumento from '../clients/ClienteConDocumento';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TIPI_APP = { isee: 'ISEE', 730: '730/740 Redditi' };
export const COLORE_TIPO = { isee: 'bg-blue-100 border-blue-300 text-blue-900', 730: 'bg-orange-100 border-orange-300 text-orange-900' };
const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
export const quando = (iso) => {
  if (!iso) return '';
  const [d, t] = iso.split('T'); const dt = new Date(`${d}T00:00`);
  return `${GIORNI[dt.getDay()]} ${d.split('-').reverse().join('/')} alle ${t}`;
};
const oggiIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// giorno + orari liberi di quel giorno
export function SceltaOrario({ valore, onCambia }) {
  const [giorno, setGiorno] = useState((valore || '').split('T')[0] || oggiIso());
  const [orari, setOrari] = useState(null);
  useEffect(() => {
    setOrari(null);
    axios.get(`${API}/appuntamenti/disponibilita`, { params: { dal: giorno, giorni: 1 } })
      .then((r) => setOrari(r.data.giorni[0]?.orari || [])).catch(() => setOrari([]));
  }, [giorno]);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="w-14">Giorno</Label>
        <Input type="date" className="w-48" value={giorno} min={oggiIso()} onChange={(e) => setGiorno(e.target.value)} />
      </div>
      {orari === null ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : orari.length === 0
        ? <p className="text-sm text-slate-500">Nessun orario: giorno di chiusura o già passato.</p>
        : (
          <div className="flex flex-wrap gap-1.5">
            {orari.map((o) => (
              <button key={o.inizio} type="button" disabled={o.liberi <= 0 && o.inizio !== valore} onClick={() => onCambia(o.inizio)}
                className={`px-2.5 py-1.5 rounded-md border text-sm ${valore === o.inizio ? 'bg-slate-900 text-white border-slate-900'
                  : o.liberi <= 0 ? 'bg-slate-100 text-slate-300 line-through' : 'bg-white hover:bg-slate-50'}`}>{o.ora}</button>
            ))}
          </div>
        )}
    </div>
  );
}

// Nuovo appuntamento: tipo, orario e cliente (come nelle Dimissioni, oppure già noto)
export function NuovoAppuntamento({ inizio: inizioIniziale = '', tipo: tipoIniziale = 'isee', cliente = null, onChiudi, onFatto }) {
  const ref = useRef(null);
  const [tipo, setTipo] = useState(tipoIniziale);
  const [inizio, setInizio] = useState(inizioIniziale);
  const [note, setNote] = useState('');
  const [clients, setClients] = useState([]);
  const [attesa, setAttesa] = useState(false);
  useEffect(() => {
    if (cliente) return;
    axios.get(`${API}/clients`).then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
  }, [cliente]);
  const salva = async () => {
    if (!inizio) { toast.error("Scegli giorno e ora"); return; }
    setAttesa(true);
    const clientId = cliente ? cliente.id : await ref.current?.conferma();
    if (!clientId) { setAttesa(false); return; }
    try {
      await axios.post(`${API}/appuntamenti`, { tipo, client_id: clientId, inizio, note });
      toast.success(`Appuntamento fissato: ${quando(inizio)}. Il cliente riceve l'avviso.`);
      onFatto();
    } catch (err) { toast.error(err.response?.data?.detail || 'Appuntamento non fissato'); setAttesa(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="sm:max-w-3xl h-[90vh] max-h-[90vh] overflow-y-auto flex flex-col">
        <DialogHeader>
          <DialogTitle>Nuovo appuntamento</DialogTitle>
          <DialogDescription>Il cliente riceve la conferma nell'app e per email. La pratica {TIPI_APP[tipo]} compare già nella sua pagina.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            {Object.entries(TIPI_APP).map(([k, t]) => (
              <Button key={k} type="button" variant={tipo === k ? 'default' : 'outline'} onClick={() => setTipo(k)}>{t}</Button>
            ))}
          </div>
          <SceltaOrario valore={inizio} onCambia={setInizio} />
          {inizio && <p className="text-sm text-green-700 font-medium">Scelto: {quando(inizio)}</p>}
          {cliente
            ? <div className="rounded-md border px-3 py-2 bg-slate-50"><span className="text-xs text-slate-500">Cliente</span><div className="font-medium">{cliente.nome}</div></div>
            : <ClienteConDocumento ref={ref} clients={clients} setClients={setClients} tipoDocumentoIniziale="Carta d'identità elettronica" />}
          <div><Label>Note per lo studio (facoltative)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
        <div className="flex-1" />
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={attesa}>{attesa && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Fissa appuntamento</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dettaglio: sposta, annulla, segna come fatto
export function DettaglioAppuntamento({ a, onChiudi, onFatto }) {
  const [nuovo, setNuovo] = useState('');
  const [sposto, setSposto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [attesa, setAttesa] = useState(false);
  const esegui = useCallback(async (fn, messaggio) => {
    setAttesa(true);
    try { await fn(); toast.success(messaggio); onFatto(); } catch (err) { toast.error(err.response?.data?.detail || 'Operazione non riuscita'); setAttesa(false); }
  }, [onFatto]);
  const attivo = a.stato === 'fissato';
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Appuntamento {TIPI_APP[a.tipo]}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-base"><CalendarClock className="w-5 h-5" /> <b>{quando(a.inizio)}</b></div>
          <div><span className="text-slate-500">Cliente:</span> <b>{a.client_name}</b>
            {a.client_phone && <a href={`tel:${a.client_phone}`} className="ml-2 inline-flex items-center gap-1 text-blue-700"><Phone className="w-3.5 h-3.5" />{a.client_phone}</a>}</div>
          <div className="text-slate-500">{a.creato_da === 'cliente' ? <span className="inline-flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Prenotato dal cliente nell'app</span> : `Fissato da ${a.autore || 'studio'}`}
            {a.spostato_da && ` · spostato (prima: ${quando(a.spostato_da)})`}</div>
          {a.note && <div><span className="text-slate-500">Note:</span> {a.note}</div>}
          {!attivo && <div className="font-semibold">{a.stato === 'annullato' ? `Annullato${a.annullato_da === 'cliente' ? ' dal cliente' : ' dallo studio'}${a.motivo ? ` (${a.motivo})` : ''}` : 'Completato'}</div>}
          {attivo && sposto && (
            <div className="rounded-md border p-3 space-y-2">
              <SceltaOrario valore={nuovo} onCambia={setNuovo} />
              <Button size="sm" disabled={!nuovo || attesa} onClick={() => esegui(() => axios.put(`${API}/appuntamenti/${a.id}`, { inizio: nuovo }), `Spostato a ${quando(nuovo)}: il cliente riceve l'avviso`)}>Conferma spostamento</Button>
            </div>
          )}
          {attivo && !sposto && (
            <div className="rounded-md border p-3 space-y-2">
              <Label>Motivo dell'annullamento (lo vede il cliente)</Label>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="facoltativo" />
            </div>
          )}
        </div>
        {attivo && (
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" disabled={attesa} onClick={() => esegui(() => axios.post(`${API}/appuntamenti/${a.id}/fatto`), 'Segnato come fatto')}>Il cliente è venuto</Button>
            <Button variant="outline" disabled={attesa} onClick={() => setSposto(!sposto)}>{sposto ? 'Non spostare' : 'Sposta'}</Button>
            <Button variant="destructive" disabled={attesa} onClick={() => { if (window.confirm('Annullare l\'appuntamento? Il cliente riceve l\'avviso.')) esegui(() => axios.post(`${API}/appuntamenti/${a.id}/annulla`, { motivo }), 'Appuntamento annullato'); }}>Annulla appuntamento</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
